import { Children, cloneElement, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./MosDashboard.module.css";
import { AKTIVITAETEN } from "../MOSFragebogen/MOSFragebogen";

const API = process.env.REACT_APP_API_URL;

function authHeaders() {
  try {
    const stored = JSON.parse(localStorage.getItem("AUTH_DATA") || "{}");
    return { "Content-Type": "application/json", Authorization: `Bearer ${stored.jwt}` };
  } catch { return { "Content-Type": "application/json" }; }
}

// ── Aggregation helpers ────────────────────────────────────────────────────

// Every question is optional: `total` is always all records, and records without
// an answer are reported as `noAnswer` ("Keine Antwort").

function countRadio(records, dataKey, field) {
  const counts = {};
  let answered = 0;
  for (const r of records) {
    const val = (r[dataKey] || {})[field];
    if (val) { counts[val] = (counts[val] || 0) + 1; answered++; }
  }
  return { counts, total: records.length, noAnswer: records.length - answered };
}

function countCheckbox(records, dataKey, field) {
  const counts = {};
  let answered = 0;
  for (const r of records) {
    const arr = (r[dataKey] || {})[field];
    if (Array.isArray(arr) && arr.length) {
      answered++;
      arr.forEach((v) => { counts[v] = (counts[v] || 0) + 1; });
    }
  }
  return { counts, total: records.length, noAnswer: records.length - answered };
}

// Matrix question: per activity, how many answered weniger / mehr / keine, or nothing
function countMatrix(records, dataKey, field) {
  const rows = AKTIVITAETEN.map((act) => ({ act, weniger: 0, mehr: 0, keine: 0, noAnswer: 0 }));
  for (const r of records) {
    const answers = (r[dataKey] || {})[field] || {};
    rows.forEach((row) => {
      const v = answers[row.act];
      if (v === "weniger" || v === "mehr" || v === "keine") row[v]++;
      else row.noAnswer++;
    });
  }
  return { rows, n: records.length };
}

// Strip skip instructions such as "(Bei Nein, weiter mit Frage 23)" from answer labels
const cleanLabel = (label) => label.replace(/\s*\((bei|wenn|weiter)[^)]*\)/i, "");

function avgScale(records, dataKey, field) {
  const dist = {};
  let sum = 0, n = 0;
  for (const r of records) {
    const v = Number((r[dataKey] || {})[field]);
    if (v >= 1 && v <= 10) {
      dist[v] = (dist[v] || 0) + 1;
      sum += v; n++;
    }
  }
  return { avg: n ? (sum / n).toFixed(1) : null, dist, n, total: records.length, noAnswer: records.length - n };
}

// ── Sub-components ─────────────────────────────────────────────────────────

const COLORS = ["#0ba1e2", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444", "#06b6d4", "#84cc16", "#f97316", "#ec4899", "#6366f1"];

const cardClass = (wide) => `${styles.chartCard}${wide ? " " + styles.chartWide : ""}`;

const NO_ANSWER_LABEL = "Keine Antwort";
const NO_ANSWER_COLOR = "#cbd5e1";

function BarChart({ title, counts, total, noAnswer = 0, maxItems = 12, wide = false }) {
  if (!total) return <div className={cardClass(wide)}><p className={styles.chartTitle}>{title}</p><p className={styles.noData}>Keine Daten</p></div>;
  const entries = Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, maxItems)
    .map(([label, count], i) => ({ label: cleanLabel(label), count, color: COLORS[i % COLORS.length] }));
  if (noAnswer > 0) entries.push({ label: NO_ANSWER_LABEL, count: noAnswer, color: NO_ANSWER_COLOR, muted: true });
  const max = Math.max(...entries.map((e) => e.count));
  return (
    <div className={cardClass(wide)}>
      <p className={styles.chartTitle}>{title}</p>
      <p className={styles.chartMeta}>n = {total}</p>
      <div className={styles.barList}>
        {entries.map((e) => {
          const pct = ((e.count / total) * 100).toFixed(1);
          return (
            <div key={e.label} className={styles.barRow}>
              <div className={styles.barLabelWrap}>
                <span className={`${styles.barLabel}${e.muted ? " " + styles.mutedLabel : ""}`}>{e.label}</span>
              </div>
              <div className={styles.barTrack}>
                <div
                  className={styles.barFill}
                  style={{ width: `${(e.count / max) * 100}%`, background: e.color }}
                />
              </div>
              <span className={styles.barValue}>{e.count} <span className={styles.barPct}>({pct}%)</span></span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const MATRIX_SEGMENTS = [
  { key: "weniger",  label: "Weniger",           color: "#f59e0b", text: "#fff" },
  { key: "keine",    label: "Keine Veränderung", color: "#64748b", text: "#fff" },
  { key: "mehr",     label: "Mehr",              color: "#10b981", text: "#fff" },
  { key: "noAnswer", label: NO_ANSWER_LABEL,     color: NO_ANSWER_COLOR, text: "#475569" },
];

function MatrixChart({ title, rows, n }) {
  if (!n) return <div className={`${styles.chartCard} ${styles.chartWide}`}><p className={styles.chartTitle}>{title}</p><p className={styles.noData}>Keine Daten</p></div>;
  return (
    <div className={`${styles.chartCard} ${styles.chartWide}`}>
      <p className={styles.chartTitle}>{title}</p>
      <p className={styles.chartMeta}>n = {n}</p>
      <div className={styles.matrixLegend}>
        {MATRIX_SEGMENTS.map((s) => (
          <span key={s.key} className={styles.legendItem}>
            <span className={styles.legendSwatch} style={{ background: s.color }} />{s.label}
          </span>
        ))}
      </div>
      <div className={styles.barList}>
        {rows.map((row) => (
          <div key={row.act} className={styles.barRow}>
            <div className={styles.barLabelWrap}>
              <span className={styles.barLabel}>{row.act}</span>
            </div>
            <div className={`${styles.barTrack} ${styles.stackTrack}`}>
              {MATRIX_SEGMENTS.map((s) => {
                const count = row[s.key];
                if (!count) return null;
                const pct = (count / n) * 100;
                return (
                  <div key={s.key} className={styles.stackSeg}
                    style={{ width: `${pct}%`, background: s.color, color: s.text }}
                    title={`${s.label}: ${count} (${pct.toFixed(1)}%)`}>
                    {pct >= 12 && count}
                  </div>
                );
              })}
            </div>
            <span className={styles.barValue}>
              {n - row.noAnswer} <span className={styles.barPct}>beantw.</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ScaleChart({ title, avg, dist, n, total, noAnswer = 0, left, right, wide = false }) {
  if (!total) return <div className={cardClass(wide)}><p className={styles.chartTitle}>{title}</p><p className={styles.noData}>Keine Daten</p></div>;
  const max = Math.max(...Object.values(dist), noAnswer, 1);
  const cols = [1,2,3,4,5,6,7,8,9,10].map((pt) => ({ key: pt, label: pt, count: dist[pt] || 0, color: "#0ba1e2" }));
  cols.push({ key: "na", label: "k.A.", count: noAnswer, color: NO_ANSWER_COLOR, title: NO_ANSWER_LABEL });
  return (
    <div className={cardClass(wide)}>
      <p className={styles.chartTitle}>{title}</p>
      <p className={styles.chartMeta}>
        n = {total} · Ø {avg ?? "–"}{n < total && ` (aus ${n} Antworten)`}
      </p>
      <div className={styles.scaleGrid}>
        {cols.map((c) => {
          const h = Math.round((c.count / max) * 60);
          return (
            <div key={c.key} className={styles.scaleCol} title={c.title}>
              <span className={styles.scaleCount}>{c.count || ""}</span>
              <div className={styles.scaleBar} style={{ height: h || 2, background: c.count ? c.color : "#e5eef4" }} />
              <span className={styles.scaleNum}>{c.label}</span>
            </div>
          );
        })}
      </div>
      {(left || right) && (
        <div className={styles.scaleEnds}>
          <span>1 = {left}</span>
          <span>10 = {right}</span>
        </div>
      )}
    </div>
  );
}

// Two-column grid: a card that would sit alone in its row (before a full-width
// matrix chart or at the end) is stretched across both columns.
function ChartGrid({ children }) {
  const items = Children.toArray(children);
  const wide = items.map((c) => c.type === MatrixChart);
  let col = 0;
  items.forEach((_, i) => {
    if (wide[i]) {
      if (col === 1) wide[i - 1] = true;
      col = 0;
    } else {
      col = col === 0 ? 1 : 0;
    }
  });
  if (col === 1) wide[items.length - 1] = true;

  return (
    <div className={styles.grid}>
      {items.map((c, i) => (wide[i] && c.type !== MatrixChart ? cloneElement(c, { wide: true }) : c))}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function MosDashboard() {
  const navigate  = useNavigate();
  const [records, setRecords]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res  = await fetch(`${API}/api/mos-fragebogen?with_episodes=1`, { headers: authHeaders() });
      const data = await res.json();
      setRecords(data.data ?? []);
    } catch { setError("Fehler beim Laden."); }
    finally   { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const ve           = records.filter((r) => r.vorerhebung_at);
  const allEpisodes  = records.flatMap((r) => r.episodes || []);
  // Statistics use only the newest EK of each patient, so every patient counts once.
  // Patients without a saved EK are not part of the EK statistics at all.
  const latestEpisodes = records
    .map((r) => (r.episodes || []).reduce(
      (latest, ep) => (!latest || ep.episode_number > latest.episode_number ? ep : latest), null))
    .filter(Boolean);

  // ── VE aggregations (T1) ───────────────────────────────────────────────
  const t1  = (field) => countRadio(ve, "t1_data", field);
  const t1s = (field) => avgScale(ve, "t1_data", field);

  // ── EK aggregations — newest EK per patient only ───────────────────────
  const pp  = (field) => countRadio(latestEpisodes, "pp_data", field);
  const pps = (field) => avgScale(latestEpisodes, "pp_data", field);
  const ppc = (field) => countCheckbox(latestEpisodes, "pp_data", field);

  return (
    <div className={styles.page}>
      <div className={styles.content}>

        {/* Header */}
        <div className={styles.topBar}>
          <div>
            <h2 className={styles.pageTitle}>MOS Dashboard</h2>
            <p className={styles.pageSub}>Auswertung aller gespeicherten Fragebogen</p>
          </div>
          <button className={styles.refreshBtn} onClick={load} disabled={loading} title="Aktualisieren">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
            </svg>
          </button>
        </div>

        {loading && <p className={styles.hint}>Wird geladen…</p>}
        {error   && <p className={styles.hint}>{error}</p>}

        {!loading && !error && (
          <>
            {/* Summary tiles */}
            <div className={styles.tileRow}>
              <div className={styles.tile}>
                <span className={styles.tileNum}>{records.length}</span>
                <span className={styles.tileLabel}>Gesamt</span>
                <span className={styles.tileHint}>Alle erfassten Patienten</span>
              </div>
              <div className={styles.tile}>
                <span className={styles.tileNum}>{ve.length}</span>
                <span className={styles.tileLabel}>Vorerhebung</span>
                <span className={styles.tileHint}>Patienten mit abgeschlossener Vorerhebung</span>
              </div>
              <div className={styles.tile}>
                <span className={styles.tileNum}>{allEpisodes.length}</span>
                <span className={styles.tileLabel}>EK gesamt</span>
                <span className={styles.tileHint}>Alle ausgefüllten Erfolgskontrollen (zur Information)</span>
              </div>
              <div className={styles.tile}>
                <span className={styles.tileNum}>{latestEpisodes.length}</span>
                <span className={styles.tileLabel}>Letzte EK je Patient</span>
                <span className={styles.tileHint}>Neueste Erfolgskontrolle jedes Patienten – Grundlage der EK-Statistik</span>
              </div>
            </div>

            {/* ── Vorerhebung ─────────────────────────────────────────── */}
            <h3 className={styles.sectionTitle}>I. Vorerhebung <span className={styles.sectionCount}>n = {ve.length}</span></h3>
            <ChartGrid>
              <BarChart title="Erwarten Sie durch die Schuhe weniger Wunden an Ihren Füßen?" {...t1("wundenErwartung")} />
              <BarChart title="Erwarten Sie, dass Sie durch die neue Schuhversorgung weniger Krankenhausaufenthaltstage wegen einer Wunde an den Füßen haben werden?" {...t1("krankenhausErwartung")} />
              <MatrixChart title="Erwarten Sie, dass Sie mit Ihren orthopädischen Schuhen die folgenden Aktivitäten weniger oder mehr ausüben können?" {...countMatrix(ve, "t1_data", "aktivitaeten")} />
              <ScaleChart title="Wie hässlich oder attraktiv erwarten Sie Ihre Schuhe?" left="sehr hässlich" right="sehr attraktiv" {...t1s("schuheAussehen")} />
              <BarChart title="Wie weit erwarten Sie mit den Schuhen gehen zu können?" {...t1("schuheGehstrecke")} />
              <BarChart title="Ist das mehr oder weniger als Sie aktuell gehen können?" {...t1("schuheVsAktuell")} />
              <BarChart title="Was ist Ihnen wichtiger: Dass die Schuhe gut aussehen oder Ihre Fußprobleme lösen?" {...t1("prioritaet")} />
              <ScaleChart title="Erwarten Sie, dass die Vorteile die Nachteile überwiegen?" left="auf keinen Fall" right="auf jeden Fall" {...t1s("vorteileUeberwiegen")} />
            </ChartGrid>

            {/* ── Erfolgskontrolle ────────────────────────────────────── */}
            <h3 className={styles.sectionTitle} style={{ marginTop: "2rem" }}>
              II. Erfolgskontrolle
              <span className={styles.sectionCount}>n = {latestEpisodes.length} Patienten</span>
            </h3>
            {latestEpisodes.length > 0 && (
              <p className={styles.sectionNote}>
                Ausgewertet wird pro Patient nur die zuletzt ausgefüllte Erfolgskontrolle
                ({latestEpisodes.length} von {allEpisodes.length} Erfolgskontrollen).
                Ältere Erfolgskontrollen bleiben gespeichert, fließen aber nicht in die Diagramme ein.
              </p>
            )}
            {latestEpisodes.length === 0
              ? <p className={styles.hint}>Noch keine Erfolgskontrolle-Daten vorhanden.</p>
              : (
                <ChartGrid>
                  <BarChart title="Wie weit können Sie aktuell gehen?" {...pp("gehstrecke")} />
                  <BarChart title="Im Vergleich zur Zeit vor Erhalt Ihrer orthopädischen Schuhe, hat sich Ihre Gehfähigkeit" {...pp("gehfaehigkeit")} />
                  <BarChart title="Im Vergleich zur Zeit vor Erhalt Ihrer orthopädischen Schuhe, hat sich Ihr allgemeiner Gesundheitszustand (nicht nur Ihre Füße)" {...pp("gesundheitszustand")} />
                  <BarChart title="Haben Ihre orthopädischen Schuhe zu Veränderungen an den Wunden an Ihren Füßen und/oder Knöcheln geführt? (Mehrere Antworten sind möglich)" {...ppc("wundenVer")} />
                  <BarChart title="Verbrachten Sie seit Erhalt Ihrer orthopädischen Schuhe aufgrund von Fußproblemen weniger Tage im Krankenhaus?" {...pp("krankenhaus")} />
                  <ScaleChart title="Wie hässlich oder schön finden Sie Ihre orthopädischen Schuhe?" left="sehr hässlich" right="sehr schön" {...pps("aussehen")} />
                  <BarChart title="Was denken andere über das Aussehen Ihrer orthopädischen Schuhe?" {...pp("andereMeinung")} />
                  <ScaleChart title="Wie schlecht oder gut passen Ihre orthopädischen Schuhe?" left="sehr schlecht" right="sehr gut" {...pps("passen")} />
                  <ScaleChart title="Wie schlecht oder gut können Sie mit Ihren orthopädischen Schuhen gehen?" left="sehr schlecht" right="sehr gut" {...pps("gehen")} />
                  <ScaleChart title="Können Sie mit Ihren orthopädischen Schuhen schlechter oder besser gehen als erwartet?" left="viel schlechter" right="viel besser" {...pps("gehenVsErwartet")} />
                  <ScaleChart title="Wie empfinden Sie das Gewicht Ihrer orthopädischen Schuhe?" left="zu leicht" right="zu schwer" {...pps("gewicht")} />
                  <ScaleChart title="Sind Ihre orthopädischen Schuhe leichter oder schwerer als erwartet?" left="viel leichter" right="viel schwerer" {...pps("gewichtVsErwartet")} />
                  <ScaleChart title="Wie schwierig ist es, Ihre orthopädischen Schuhe an- und auszuziehen?" left="sehr schwierig" right="sehr einfach" {...pps("anziehen")} />
                  <MatrixChart title="Konnten Sie seit Erhalt Ihrer orthopädischen Schuhe Ihre gewohnten Aktivitäten an mehr oder weniger Tagen uneingeschränkt ausüben?" {...countMatrix(latestEpisodes, "pp_data", "aktivitaeten")} />
                  <BarChart title="Haben Sie Wunden oder Geschwüre an Füßen oder Knöcheln?" {...pp("wundenAktuell")} />
                  <BarChart title="Wie oft tragen Sie Ihre orthopädischen Schuhe?" {...pp("trageHaeufigkeit")} />
                  <BarChart title="Wenn Sie Ihre orthopädischen Schuhe tragen: Wie viele Stunden pro Tag?" {...pp("stunden")} />
                  <BarChart title="Tragen Sie Ihre orthopädischen Schuhe so oft wie erwartet?" {...pp("tragenWieErwartet")} />
                  <ScaleChart title="Wie zufrieden sind Sie mit der Häufigkeit und Dauer der Nutzung Ihrer orthopädischen Schuhe?" left="sehr unzufrieden" right="sehr zufrieden" {...pps("nutzungZufrieden")} />
                  <BarChart title="Was ist Ihnen wichtiger: Dass die Schuhe gut aussehen oder Ihre Fußprobleme lösen?" {...pp("prioritaet")} />
                  <ScaleChart title="Überwiegen die Vorteile die Nachteile?" left="auf keinen Fall" right="auf jeden Fall" {...pps("vorteileUeberwiegen")} />
                  <BarChart title="Haben Ihre orthopädischen Schuhe Ihre Ziele erfüllt?" {...pp("zieleErfuellt")} />
                </ChartGrid>
              )
            }
          </>
        )}

        <div className={styles.actionsRow}>
          <button type="button" className={styles.primaryBtn} onClick={() => navigate(-1)}>Zurück</button>
        </div>
      </div>
    </div>
  );
}
